/**
 * Configuration centralisée des permissions
 * Ce fichier contient toutes les définitions de permissions pour l'application
 */

export type UserRole =
    | 'Admin'
    | 'Responsable pedagogique'
    | 'Secretaire'
    | 'Enseignant'
    | 'Etudiant'

export type UserGroup =
    | 'Administration'
    | 'Responsable pedagogique'
    | 'Pedagogie'
    | 'Secretariat'
    | 'Enseignement'
    | 'Enseignant'
    | 'Etudiants'
    | 'Etudiant'
    | 'Direction'
    | 'Comptabilite'
    | 'Ressources Humaines'
    | 'RH'
    | 'Maintenance'

export type PermissionAction =
// Permissions étudiants
    | 'create_student'
    | 'update_student'
    | 'list_student'
    | 'delete_student'
    | 'export_student'
    | 'import_student'
    | 'mass_enrollment'

    // Permissions cours
    | 'create_course'
    | 'update_course'
    | 'delete_course'
    | 'list_course'

    // Permissions enseignants
    | 'create_teacher'
    | 'update_teacher'
    | 'delete_teacher'
    | 'list_teacher'

    // Permissions académiques
    | 'manage_grades'
    | 'view_reports'

    // Permissions système
    | 'manage_system'

/**
 * Matrice des permissions par rôle
 * Chaque action est associée aux rôles qui peuvent l'effectuer
 */
export const PERMISSIONS_MATRIX: Record<PermissionAction, UserRole[]> = {
    // === PERMISSIONS ÉTUDIANTS ===
    create_student: ['Responsable pedagogique', 'Admin', 'Secretaire'],
    update_student: ['Responsable pedagogique', 'Admin', 'Secretaire'],
    list_student: ['Responsable pedagogique', 'Admin', 'Secretaire', 'Enseignant'],
    delete_student: ['Admin'], // Seuls les admins peuvent supprimer définitivement
    export_student: ['Responsable pedagogique', 'Admin', 'Secretaire', 'Enseignant'],
    import_student: ['Admin', 'Secretaire'], // Import sensible
    mass_enrollment: ['Responsable pedagogique', 'Admin', 'Secretaire'],

    // === PERMISSIONS COURS ===
    create_course: ['Responsable pedagogique', 'Admin'],
    update_course: ['Responsable pedagogique', 'Admin', 'Enseignant'],
    delete_course: ['Admin'],
    list_course: ['Responsable pedagogique', 'Admin', 'Enseignant', 'Etudiant'],

    // === PERMISSIONS ENSEIGNANTS ===
    create_teacher: ['Admin'],
    update_teacher: ['Admin', 'Responsable pedagogique'],
    delete_teacher: ['Admin'],
    list_teacher: ['Admin', 'Responsable pedagogique', 'Enseignant'],

    // === PERMISSIONS ACADÉMIQUES ===
    manage_grades: ['Responsable pedagogique', 'Admin', 'Enseignant'],
    view_reports: ['Responsable pedagogique', 'Admin', 'Enseignant'],

    // === PERMISSIONS SYSTÈME ===
    manage_system: ['Admin']
}

/**
 * Matrice des permissions par groupe
 * Chaque action est associée aux groupes qui peuvent l'effectuer
 */
export const PERMISSIONS_BY_GROUP: Record<PermissionAction, UserGroup[]> = {
    // === PERMISSIONS ÉTUDIANTS ===
    create_student: ['Administration', 'Pedagogie', 'Secretariat'],
    update_student: ['Administration', 'Pedagogie', 'Secretariat'],
    list_student: ['Administration', 'Pedagogie', 'Secretariat', 'Enseignement'],
    delete_student: ['Administration'], // Seuls les admins peuvent supprimer définitivement
    export_student: ['Administration', 'Pedagogie', 'Secretariat', 'Enseignement'],
    import_student: ['Administration', 'Secretariat'], // Import sensible
    mass_enrollment: ['Administration', 'Pedagogie', 'Secretariat'],

    // === PERMISSIONS COURS ===
    create_course: ['Administration', 'Pedagogie'],
    update_course: ['Administration', 'Pedagogie', 'Enseignement'],
    delete_course: ['Administration'],
    list_course: ['Administration', 'Pedagogie', 'Enseignement', 'Etudiants'],

    // === PERMISSIONS ENSEIGNANTS ===
    create_teacher: ['Administration'],
    update_teacher: ['Administration', 'Pedagogie'],
    delete_teacher: ['Administration'],
    list_teacher: ['Administration', 'Pedagogie', 'Enseignement'],

    // === PERMISSIONS ACADÉMIQUES ===
    manage_grades: ['Administration', 'Pedagogie', 'Enseignement'],
    view_reports: ['Administration', 'Pedagogie', 'Enseignement'],

    // === PERMISSIONS SYSTÈME ===
    manage_system: ['Administration']
}

/**
 * Mapping des rôles vers les groupes
 * Chaque rôle appartient à un ou plusieurs groupes
 */
export const ROLE_TO_GROUPS: Record<UserRole, UserGroup[]> = {
    'Admin': ['Administration', 'Direction'],
    'Responsable pedagogique': ['Pedagogie', 'Direction'],
    'Secretaire': ['Secretariat', 'Administration'],
    'Enseignant': ['Enseignement'],
    'Etudiant': ['Etudiants']
}

/**
 * Mapping des groupes vers les rôles
 * Chaque groupe contient un ou plusieurs rôles
 */
export const GROUP_TO_ROLES: Record<UserGroup, UserRole[]> = {
    'Administration': ['Admin', 'Secretaire'],
    'Responsable pedagogique': ['Responsable pedagogique'], // Variante (mappée vers Pedagogie)
    'Pedagogie': ['Responsable pedagogique'],
    'Secretariat': ['Secretaire'],
    'Enseignement': ['Enseignant'],
    'Enseignant': ['Enseignant'], // Variante (mappée vers Enseignement)
    'Etudiants': ['Etudiant'],
    'Etudiant': ['Etudiant'], // Variante (mappée vers Etudiants)
    'Direction': ['Admin', 'Responsable pedagogique'],
    'Comptabilite': ['Secretaire'],
    'Ressources Humaines': ['Admin', 'Secretaire'],
    'RH': ['Admin', 'Secretaire'], // RH est un alias de Ressources Humaines
    'Maintenance': ['Admin'] // Par défaut, on rattache Maintenance à un rôle fort pour les contrôles éventuels
}

/**
 * Groupes de permissions pour faciliter la gestion
 */
export const PERMISSION_GROUPS = {
    STUDENT_MANAGEMENT: [
        'create_student',
        'update_student',
        'list_student',
        'delete_student',
        'export_student',
        'import_student',
        'mass_enrollment'
    ] as PermissionAction[],

    COURSE_MANAGEMENT: [
        'create_course',
        'update_course',
        'delete_course',
        'list_course'
    ] as PermissionAction[],

    TEACHER_MANAGEMENT: [
        'create_teacher',
        'update_teacher',
        'delete_teacher',
        'list_teacher'
    ] as PermissionAction[],

    ACADEMIC_MANAGEMENT: [
        'manage_grades',
        'view_reports'
    ] as PermissionAction[],

    SYSTEM_MANAGEMENT: [
        'manage_system'
    ] as PermissionAction[]
}

/**
 * Hiérarchie des rôles (du plus privilégié au moins privilégié)
 */
export const ROLE_HIERARCHY: UserRole[] = [
    'Admin',
    'Responsable pedagogique',
    'Secretaire',
    'Enseignant',
    'Etudiant'
]

/**
 * Vérifier si un rôle a plus de privilèges qu'un autre
 */
export const hasHigherPrivileges = (role1: UserRole, role2: UserRole): boolean => {
    const index1 = ROLE_HIERARCHY.indexOf(role1)
    const index2 = ROLE_HIERARCHY.indexOf(role2)
    return index1 < index2 // Plus l'index est bas, plus le rôle est privilégié
}

/**
 * Obtenir tous les rôles avec des privilèges égaux ou supérieurs
 */
export const getRolesWithEqualOrHigherPrivileges = (role: UserRole): UserRole[] => {
    const roleIndex = ROLE_HIERARCHY.indexOf(role)
    return ROLE_HIERARCHY.slice(0, roleIndex + 1)
}

/**
 * Obtenir tous les rôles avec des privilèges inférieurs
 */
export const getRolesWithLowerPrivileges = (role: UserRole): UserRole[] => {
    const roleIndex = ROLE_HIERARCHY.indexOf(role)
    return ROLE_HIERARCHY.slice(roleIndex + 1)
}
